/**
 * Turf Pricing Utilities
 * 
 * Functions for calculating turf program prices based on database pricing tiers
 * and markup percentages - matching the legacy system functionality.
 */

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const PROGRAM_META = {
  1: { key: 'simple3', setting: 'simple_3_price', muField: 'simple3MU', name: '3-Step Program', applications: 3 },
  2: { key: 'hybrid5', setting: 'hybrid_5_price', muField: 'hybrid5MU', name: '5-Step Program', applications: 5 },
  3: { key: 'premium6', setting: 'premium_6_price', muField: 'premium6MU', name: '6-Step Premium', applications: 6 },
  4: { key: 'ultra6', setting: 'ultra_6_price', muField: 'ultra6MU', name: '6-Step Ultra', applications: 6 }
};

/**
 * Fetch base prices and settings for a given square footage
 * @param {number} squareFt - Property square footage
 * @param {number} overSeedMU - Overseed markup % used for specialty treatment formulas
 * @returns {Promise<Object>} Base prices and markup settings
 */
export const getBasePricesForSquareFt = async (squareFt, overSeedMU = 40) => {
  try {
    const token = localStorage.getItem('token');
    const mu = encodeURIComponent(overSeedMU ?? 40);
    const response = await fetch(`/api/admin/turf/calculate-base-prices/${squareFt}?overSeedMU=${mu}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch base prices');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching base prices:', error);
    return {
      turf: 0,
      aeration: 0,
      grub: 0,
      addons: {},
      settings: {
        simple_3_price: 40,
        hybrid_5_price: 40,
        premium_6_price: 40,
        ultra_6_price: 40,
        aeration_price: 40,
        grub_price: 40
      }
    };
  }
};

const resolveMarkup = (override, fallback) => {
  const parsed = parseFloat(override);
  if (!isNaN(parsed)) return parsed;
  const fromSettings = parseFloat(fallback);
  return isNaN(fromSettings) ? 40 : fromSettings;
};

/**
 * Calculate turf program pricing based on program type and base prices
 * @param {number} programType - 1=simple3, 2=hybrid5, 3=premium6, 4=ultra6
 * @param {Object} basePrices - Base prices from database
 * @param {Object} propertyInfo - Property information including square footage
 * @param {number|null} markupOverride - Quote-level MU % when the user has edited it
 * @returns {Object} Calculated pricing information
 */
export const calculateTurfProgramPrice = (programType, basePrices, propertyInfo = {}, markupOverride = null) => {
  if (!programType || !basePrices) return { price: 0, markup: 40 };

  const meta = PROGRAM_META[programType];
  if (!meta) return { price: 0, markup: 40, programName: 'None' };

  const { turf: basePrice, settings = {} } = basePrices;
  const markupPercentage = resolveMarkup(markupOverride, settings[meta.setting]);
  // Legacy (PricesHelper.CalculateTurfPrice): the M/U is the whole-season
  // multiplier applied to the single-application base price, e.g. 782 means
  // "seasonal price = 782% of one application" (6 apps x ~130% markup already
  // baked in). It is NOT (1 + MU) and it is NOT multiplied by the application
  // count again - doing both was inflating Premium(6) from $328.44 to $2,222.64.
  const finalPrice = (Number(basePrice) || 0) * (markupPercentage / 100);
  const pricePerApplication = meta.applications > 0 ? finalPrice / meta.applications : 0;

  return {
    price: roundMoney(finalPrice),
    pricePerApplication: roundMoney(pricePerApplication),
    applications: meta.applications,
    markup: markupPercentage,
    basePrice: basePrice,
    programName: meta.name,
    key: meta.key
  };
};

/**
 * Calculate aeration pricing
 * @param {Object} basePrices - Base prices from database
 * @param {number|null} priceOverride - Quote-level price when the user has edited it
 * @returns {Object} Calculated aeration pricing
 */
export const calculateAerationPrice = (basePrices, priceOverride = null) => {
  if (!basePrices) return { price: 0, markup: 40 };

  const { aeration: basePrice, settings = {} } = basePrices;
  const markupPercentage = parseFloat(settings.aeration_price) || 40;
  // Legacy: aeration = base * aeration_percent / 100 (110 => 1.10x), not (1 + %).
  const calculated = (Number(basePrice) || 0) * (markupPercentage / 100);
  const override = parseFloat(priceOverride);

  return {
    price: roundMoney(!isNaN(override) && override > 0 ? override : calculated),
    calculatedPrice: roundMoney(calculated),
    markup: markupPercentage,
    basePrice: basePrice
  };
};

/**
 * Calculate grub control pricing
 * @param {Object} basePrices - Base prices from database
 * @param {number|null} priceOverride - Quote-level price when the user has edited it
 * @returns {Object} Calculated grub pricing
 */
export const calculateGrubPrice = (basePrices, priceOverride = null) => {
  if (!basePrices) return { price: 0, markup: 40 };

  const { grub: basePrice, settings = {} } = basePrices;
  const markupPercentage = parseFloat(settings.grub_price) || 40;
  // Legacy: grub = base * grub_percent / 100 (100 => 1.00x), not (1 + %).
  const calculated = (Number(basePrice) || 0) * (markupPercentage / 100);
  const override = parseFloat(priceOverride);

  return {
    price: roundMoney(!isNaN(override) && override > 0 ? override : calculated),
    calculatedPrice: roundMoney(calculated),
    markup: markupPercentage,
    basePrice: basePrice
  };
};

const addonDisplay = (key, name, calculated, override) => {
  const custom = parseFloat(override);
  const calc = Number(calculated) || 0;
  return {
    name,
    key,
    price: roundMoney(!isNaN(custom) && custom > 0 ? custom : calc),
    calculatedPrice: roundMoney(calc)
  };
};

/**
 * Calculate total turf pricing for a quote
 * @param {Object} services - Services configuration from quote form
 * @param {Object} propertyInfo - Property information
 * @returns {Promise<Object>} Complete turf pricing breakdown
 */
export const calculateTotalTurfPricing = async (services, propertyInfo = {}) => {
  if (!services.turf.enabled) {
    return {
      total: 0,
      breakdown: {},
      programName: 'None'
    };
  }

  const turf = services.turf;
  const squareFt = propertyInfo.squareFt || 1000;
  const basePrices = await getBasePricesForSquareFt(squareFt, turf.overSeedMU);

  let total = 0;
  const breakdown = { programs: {} };

  Object.entries(PROGRAM_META).forEach(([id, meta]) => {
    const useQuoteMU = turf.muInitialized;
    const override = useQuoteMU ? turf[meta.muField] : null;
    breakdown.programs[meta.key] = calculateTurfProgramPrice(Number(id), basePrices, propertyInfo, override);
  });

  // Main turf program
  if (turf.program > 0) {
    const meta = PROGRAM_META[turf.program];
    const programPricing = breakdown.programs[meta.key];
    total += programPricing.price;
    breakdown.program = programPricing;
  }

  const grubPricing = calculateGrubPrice(basePrices, turf.grubPrice);
  breakdown.grubControl = {
    name: 'Grub Control Treatment',
    ...grubPricing
  };
  if (turf.grubControl) {
    total += grubPricing.price;
  }

  const aerationPricing = calculateAerationPrice(basePrices, turf.aerationPrice);
  breakdown.aeration = {
    name: 'Spring or Fall Core Aeration',
    ...aerationPricing
  };
  if (turf.aeration) {
    total += aerationPricing.price;
  }

  const addons = basePrices.addons || {};
  const soilTest = addonDisplay('soilTest', 'Soil Test - Core Sample', addons.soilTest || 85, turf.soilTestPrice);
  breakdown.soilTest = soilTest;
  if (turf.soilTest) {
    total += soilTest.price;
  }

  breakdown.saltStopper = addonDisplay('saltStopper', 'Salt Stopper', addons.saltStopper, turf.saltStopperPrice);
  breakdown.kmag = addonDisplay('kmag', 'K-Mag', addons.kmag, turf.kmagPrice);
  breakdown.pHIncreaser = addonDisplay('pHIncreaser', 'pH Increaser', addons.pHIncreaser, turf.pHIncreaserPrice);
  breakdown.pHDecreaser = addonDisplay('pHDecreaser', 'pH Decreaser', addons.pHDecreaser, turf.pHDecreaserPrice);
  breakdown.trEliteSeed = addonDisplay('trEliteSeed', 'TR Elite Seed', addons.trEliteSeed, turf.trEliteSeedPrice);
  breakdown.timeTopdressing = addonDisplay('timeTopdressing', "Time Sav'r Topdressing", addons.timeTopdressing, turf.timeTopdressingPrice);
  breakdown.irrigation = addonDisplay(
    'irrigation',
    'Irrigation System Open and Close',
    turf.irrigationBasePrice || addons.irrigation || 175,
    null
  );

  // Specialty treatments are added by the backend pricing endpoint so they
  // are not written into turf.sum here (avoids double-counting on save).
  const selectedAddonsTotal = (
    (turf.saltStopper ? breakdown.saltStopper.price : 0) +
    (turf.kmag ? breakdown.kmag.price : 0) +
    (turf.pHIncreaser ? breakdown.pHIncreaser.price : 0) +
    (turf.pHDecreaser ? breakdown.pHDecreaser.price : 0) +
    (turf.trEliteSeed ? breakdown.trEliteSeed.price : 0) +
    (turf.timeTopdressing ? breakdown.timeTopdressing.price : 0) +
    (turf.irrigation ? breakdown.irrigation.price : 0)
  );

  const disposalFee = Number(turf.disposalFee) || 0;

  return {
    total: roundMoney(total),
    displayTotal: roundMoney(total + selectedAddonsTotal + disposalFee),
    disposalFee: roundMoney(disposalFee),
    breakdown,
    programName: breakdown.program?.name || 'None'
  };
};

/**
 * Format pricing breakdown for display
 * @param {Object} pricingBreakdown - Result from calculateTotalTurfPricing
 * @returns {Array} Formatted items for display
 */
export const formatTurfPricingForDisplay = (pricingBreakdown) => {
  const items = [];

  if (pricingBreakdown.breakdown.program) {
    const program = pricingBreakdown.breakdown.program;
    items.push({
      name: program.name,
      description: `$${program.pricePerApplication.toFixed(2)} x ${program.applications}`,
      price: program.price
    });
  }

  Object.entries(pricingBreakdown.breakdown).forEach(([key, item]) => {
    if (key !== 'program' && key !== 'programs' && item && item.price != null) {
      items.push({
        name: item.name,
        description: item.basePrice ? `Base: $${Number(item.basePrice).toFixed(2)} x ${item.markup}%` : 'Fixed price',
        price: item.price
      });
    }
  });

  return items;
};

/**
 * Migrate legacy manual pricing to new automatic system
 * @param {Object} legacyServices - Existing services with manual prices
 * @param {Object} propertyInfo - Property information
 * @returns {Promise<Object>} Updated services with automatic pricing
 */
export const migrateLegacyTurfPricing = async (legacyServices, propertyInfo = {}) => {
  if (!legacyServices.turf.enabled) return legacyServices;

  const updatedServices = { ...legacyServices };
  
  // Determine program type based on selected checkboxes
  let programType = 0;
  if (updatedServices.turf.simple3Selected) programType = 1;
  else if (updatedServices.turf.hybrid5Selected) programType = 2;
  else if (updatedServices.turf.premium6Selected) programType = 3;
  else if (updatedServices.turf.ultra6Selected) programType = 4;

  // Update program field
  updatedServices.turf.program = programType;

  // Calculate new pricing
  const pricing = await calculateTotalTurfPricing(updatedServices, propertyInfo);
  updatedServices.turf.sum = pricing.total;
  updatedServices.turf.baseSum = pricing.total;

  return updatedServices;
};
